import { useMemo } from "react";
import { playbackNowTimestamp } from "../../../../../utils/timeManager";
import {
  SlotManagerProps,
  DynamicRenderSlot,
} from "../types/dynamic-viewer-types";

/**
 * Custom hook to manage dynamic render slots with reuse optimization
 */
export function useSlotManager({
  bpItems,
  currentIndex,
  currentBpItem,
  nextBpItem,
  timeOptions,
  renderSlotsRef,
}: SlotManagerProps): DynamicRenderSlot[] {
  return useMemo((): DynamicRenderSlot[] => {
    if (bpItems.length === 0) {
      return [];
    }

    const currentTime = timeOptions
      ? playbackNowTimestamp(timeOptions)
      : Date.now();

    // Get previous slots before clearing for reuse optimization
    const previousSlots = renderSlotsRef.current
      ? [...renderSlotsRef.current]
      : [];

    // Create new array for current slots
    const slots: DynamicRenderSlot[] = [];

    // Single item case: no preload slot needed
    if (bpItems.length === 1) {
      if (currentBpItem) {
        const activeSlot: DynamicRenderSlot = {
          item: currentBpItem,
          indexInBpItems: currentIndex,
          isPreload: false,
          hasNextItem: false, // No next item when only 1 item
        };

        // Update the item's timing for active state
        if (activeSlot.item) {
          activeSlot.item = { ...activeSlot.item };
          activeSlot.item.showDurationMs = activeSlot.item.fullDurationMs;
          activeSlot.item.startTimestampMs = currentTime;
        }

        slots[0] = activeSlot; // Place at index 0
      }
      return slots;
    }

    // Multiple items case: check for slot reuse optimization
    let activeSlot: DynamicRenderSlot | undefined;
    let preloadSlot: DynamicRenderSlot | undefined;
    let activeSlotIndex = 0; // Default active slot index
    let preloadSlotIndex = 1; // Default preload slot index

    // Check if currentBpItem matches any previous slot for reuse
    let reusedSlotIndex = -1;

    if (previousSlots && previousSlots.length > 0) {
      for (let i = 0; i < previousSlots.length; i++) {
        const previousSlot = previousSlots[i];
        if (
          previousSlot?.item &&
          currentBpItem &&
          previousSlot.item.id === currentBpItem.id &&
          previousSlot.item.type === currentBpItem.type
        ) {
          reusedSlotIndex = i;
          break;
        }
      }
    }

    if (reusedSlotIndex !== -1) {
      // REUSE: Keep the slot at its original index, just update properties
      activeSlot = {
        ...previousSlots[reusedSlotIndex], // Reuse the existing slot object completely
        isPreload: false, // Only change this flag
        indexInBpItems: currentIndex, // Update index
        hasNextItem: nextBpItem ? true : false,
      };

      // Keep the slot at its original index
      activeSlotIndex = reusedSlotIndex;
      preloadSlotIndex = reusedSlotIndex === 0 ? 1 : 0;
    } else if (currentBpItem) {
      // CREATE NEW: No reusable slot found, create new active slot
      activeSlot = {
        item: currentBpItem,
        indexInBpItems: currentIndex,
        isPreload: false,
        hasNextItem: nextBpItem ? true : false,
      };

      // Update the item's timing for active state
      if (activeSlot.item) {
        activeSlot.item = { ...activeSlot.item };
        activeSlot.item.showDurationMs = activeSlot.item.fullDurationMs;
        activeSlot.item.startTimestampMs = currentTime;
      }

      // For new active slots, use default positioning (index 0)
      activeSlotIndex = 0;
      preloadSlotIndex = 1;
    }

    // Create preload slot for next item (if available and different from active)
    if (nextBpItem && nextBpItem.id !== currentBpItem?.id) {
      preloadSlot = {
        item: nextBpItem,
        indexInBpItems: (currentIndex + 1) % bpItems.length,
        isPreload: true,
        hasNextItem: bpItems.length > 1, // Will be false when bpItems.length === 1
      };

      // Set preload timing - these will be preserved when slot becomes active
      if (preloadSlot.item) {
        preloadSlot.item = { ...preloadSlot.item };
        preloadSlot.item.showDurationMs = preloadSlot.item.fullDurationMs; // Keep full duration
        preloadSlot.item.startTimestampMs =
          currentTime + (currentBpItem?.fullDurationMs || 0); // Set future start time
      }
    }

    // Place slots at their determined indices
    if (activeSlot) {
      slots[activeSlotIndex] = activeSlot;
    }
    if (preloadSlot) {
      slots[preloadSlotIndex] = preloadSlot;
    }

    // Store current slots for next cycle's reuse optimization
    renderSlotsRef.current = slots;

    return slots;
  }, [
    bpItems,
    currentIndex,
    currentBpItem,
    nextBpItem,
    timeOptions,
    renderSlotsRef,
  ]);
}
